import {
  Component,
  DestroyRef,
  inject,
  signal,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { Paginator } from 'primeng/paginator';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { ConversionListingService } from '../../services/common/conversion-listing.service';
import {
  DownloadXbrlService,
  XbrlDownloadApiError,
} from '../../services/common/download-xbrl.service';
import { conversionActionFailureCopy } from '../../utils/conversion-error-codes.util';
import { filingEntryState } from '../../utils/navigation-diagnostic-scope.util';
import { RuntimeConfigService } from '../../services/common/runtime-config.service';
import { debounceTime, finalize, merge, Subject } from 'rxjs';
import {
  ConversionData,
  ConversionProgressPayload,
  MbrsStatusEnum,
} from '../../../types/commons';
import { ConversionStatusResolverService } from '../../services/mbrs/conversion-status-resolver.service';
import {
  isCompanyClickable,
  isDownloadable,
  isMoreActionButtonVisible,
} from '../../utils/conversion-status.util';
import { Router, RouterLink } from '@angular/router';
import { ConversionEventService } from '../../services/common/conversion-event.service';
import { InteractiveToastComponent } from '../common/interactive-toast/interactive-toast.component';
import { MessageService } from 'primeng/api';
import { OverlayModule } from '@angular/cdk/overlay';
import { DeleteConversionComponent } from '../common/delete-conversion/delete-conversion.component';
import { OverflowTooltipDirective } from '../../directives/overflow-tooltip.directive';
import { SEARCH_INPUT_DEBOUNCE_MS } from '../../utils/debounce-times.util';
import { ConversionProgressService } from '../../services/signalr/conversion-progress.service';
import { DebugLayoutToggleService } from '../../services/common/debug-layout-toggle.service';
import { EnvironmentService } from '../../services/common/environment.service';
import { XbrlFilesDebugLayoutComponent } from './xbrl-files-debug-layout/xbrl-files-debug-layout.component';
import { XbrlDownloadNameError } from '../../utils/xbrl-download-naming.util';
import { TOAST_DURATION_EXTENDED_MS } from '../../utils/toast-durations.util';
import { DIAGNOSTIC_TOAST_LIFE_MS } from '../../utils/sheet-load-trace.util';
import { AccessDeniedNoticeService } from '../../services/common/access-denied-notice.service';
import { buildPageLoadFailureNotice } from '../../utils/page-load-failure.util';
import { PageLoadFailureToastGate } from '../../utils/page-load-failure-toast-gate.util';
import { AuthFailureNoticeService } from '../../services/auth/auth-failure-notice.service';
import { environment } from '../../../environments/environment';
// TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
import {
  dumpLivenessDiagnostics,
  clearLivenessDiagnostics,
  isLivenessDiagnosticsEnabled,
} from '../../services/mbrs/liveness-probe-diagnostics';

/**
 * Parse the optional `updatedAt` off a SignalR progress payload, returning
 * `null` for anything the caller should not paint into the listing.
 *
 * The guard is not defensive padding: `new Date(undefined)` and
 * `new Date('not-a-date')` both yield an `Invalid Date`, which the `| date`
 * pipe renders as an empty cell — silently blanking "Last Modified" instead of
 * advancing it. Returning `null` lets the caller fall back to local time.
 */
function parseSignalRTimestamp(raw: string | undefined): Date | null {
  if (!raw)
    return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

@Component({
  selector: 'xbrl-files',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    SelectModule,
    Paginator,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DatePickerModule,
    SkeletonModule,
    InteractiveToastComponent,
    OverlayModule,
    DeleteConversionComponent,
    OverflowTooltipDirective,
    RouterLink,
    XbrlFilesDebugLayoutComponent,
  ],
  providers: [MessageService],
  templateUrl: './xbrl-files.component.html',
})
export class XbrlFilesComponent implements OnInit, AfterViewInit {
  conversionListingService = inject(ConversionListingService);
  downloadXbrlService = inject(DownloadXbrlService);
  conversionEvent = inject(ConversionEventService);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  fullData = signal<ConversionData[]>([]);
  totalRecords = signal<number>(0);
  isTableLoading = signal<boolean>(false);
  conversionId = signal<string>('');
  router = inject(Router);
  messageService = inject(MessageService);
  authFailureNotice = inject(AuthFailureNoticeService);
  accessDeniedNotice = inject(AccessDeniedNoticeService);
  conversionProgressService = inject(ConversionProgressService);
  private statusResolver = inject(ConversionStatusResolverService);
  private destroyRef = inject(DestroyRef);
  protected debugToggle = inject(DebugLayoutToggleService);
  protected envService = inject(EnvironmentService);
  conversionData!: ConversionData;

  // AS-842: conversion IDs whose terminal toast has already fired in this
  // view. Three paths can surface a terminal toast for the same row and must
  // not double up:
  //   1. the post-submit redirect (ngAfterViewInit, `pendingValidation` state),
  //   2. the listing fetch `forEach` -> `maybeKickOffPendingValidation`,
  //   3. the SignalR `progress2nd$` ping -> `maybeKickOffPendingValidation`.
  // The resolver de-dupes its validate+POST work, but each subscriber still
  // runs its callback, so this guard is what makes the *toast* fire once. It
  // gates only the toast — the resolve still runs each time so a failed POST
  // can self-heal (chip flip + retry) on the next listing reload without
  // re-toasting. An entry is cleared when the row re-enters a fresh conversion
  // cycle (see the `InProgress` reset in the progress handler).
  private readonly terminalToastFired = new Set<string>();

  // Caps this page's load-failure toast to one per class — see
  // PageLoadFailureToastGate's own docblock. Applied uniformly across every
  // page-load-failure.util.ts consumer, not only the pages with more than
  // one load-up API call, so the same class of bug can't resurface quietly
  // the moment a second call is added here.
  private readonly loadFailureToastGate = new PageLoadFailureToastGate(
    undefined,
    // REFACTORED-DIAGNOSTIC-CODES-FLAG: silent page-level toasts in production.
    () => this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
  );

  // AS-842: the just-submitted conversion (from SubmitForValidation's
  // navigation state), captured in `ngOnInit` before the first listing fetch.
  // The fetch subscribe fires its terminal toast deterministically once the
  // row's real status is known — replacing an earlier `setTimeout(800)` guess
  // that raced the fetch and fired a false "ready for download" toast when
  // `fullData()` was still empty. One-shot: cleared after the first response.
  private postSubmitPending: { fileName: string; conversionId: string } | null =
    null;

  // AS-842: row-interactivity gates (templates call these instead of direct
  // status comparisons — see src/app/utils/conversion-status.util.ts). The
  // download gate is exposed via the existing `isDownloadable(status)` method
  // below so the template signature stays the same after the inversion fix.
  // `isMoreActionButtonVisible` is likewise a method rather than a bare alias,
  // because it now has to feed the runtime-config flag through.
  protected readonly isCompanyClickable = isCompanyClickable;
  protected readonly filingEntryState = filingEntryState;

  // AS-842: filter dropdown surfaces every terminal as its own option so the
  // user can split clean Success rows from Success* (warnings) rows. Backend
  // does exact-match filtering on the enum value. PendingValidation is NOT
  // listed because the user only ever sees it as "In Progress" — exposing
  // both would be confusing. The "In Progress" filter therefore matches
  // enum=2 only; PendingValidation rows resolve to a terminal within seconds
  // and become visible under the matching terminal filter.
  conversionStatuses = [
    { label: 'Success', value: MbrsStatusEnum.Success },
    { label: 'Success*', value: MbrsStatusEnum.SuccessWithWarnings },
    { label: 'Failed', value: MbrsStatusEnum.Failed },
    { label: 'Review', value: MbrsStatusEnum.Review },
    { label: 'In Progress', value: MbrsStatusEnum.InProgress },
    { label: 'All Statuses', value: '' },
  ];

  searchQuery = signal<string>('');
  selectedStatus = signal<MbrsStatusEnum | string>('');
  // The picker models MUST be `null` when empty, never `[]`. PrimeNG's
  // `isYearSelected()` guards with `value != null`, which an empty array passes;
  // it then reads `value[0].getFullYear()` in range mode and throws on
  // `undefined`, killing the year-picker view. An empty array therefore renders
  // a date picker whose "choose year" button silently does nothing.
  //
  // Conversion-date range (backend: Conversion.CreatedAt).
  startDate = signal<string | null>(null);
  endDate = signal<string | null>(null);
  selectedDate = signal<Date[] | null>(null);
  // Last-modified range (backend: Conversion.UpdatedAt). Independent of the
  // range above — both may be active at once and are AND-ed by the backend.
  modifiedStartDate = signal<string | null>(null);
  modifiedEndDate = signal<string | null>(null);
  selectedModifiedDate = signal<Date[] | null>(null);
  sortBy = signal<string>('');
  isDescending = signal<boolean>(true);
  first = signal<number>(0);
  /**
   * Rows per page. Server-side: this value is sent to the backend as
   * `pageSize` on every fetch — it does not slice a client-side array
   * (the table sets `[paginator]="false"`). Sourced from runtime config
   * (`listingPageSize.xbrlFiles`) so ops can tune it per-deployment
   * without a rebuild; committed default is 15.
   */
  rows = signal<number>(this.runtimeConfig.xbrlFilesPageSize());
  private searchChanged = new Subject<string>();
  MbrsStatusEnum = MbrsStatusEnum;
  openedOverlayId = signal<number | null>(null);
  isArchiveDialogOpen = false;

  private fetchConversionListing(page?: number, pageSize?: number) {
    this.isTableLoading.set(true);

    this.conversionListingService
      // AS-842: when the user filters "In Progress", the backend matches
      // enum=2 only — `PendingValidation` (=5) rows are excluded. Acceptable
      // because the silent-validation window is short; rows resolve to a
      // terminal status within seconds. If the listing needs to surface
      // PendingValidation under "In Progress" exactly, the backend would need
      // to accept a multi-status filter (out of scope for AS-842 v1).
      .fetchConversionListing({
        page: page ?? 1,
        pageSize: pageSize ?? this.rows(),
        searchTerm: this.searchQuery(),
        status: this.selectedStatus(),
        fromDate: this.startDate() ?? '',
        toDate: this.endDate() ?? '',
        modifiedFromDate: this.modifiedStartDate() ?? '',
        modifiedToDate: this.modifiedEndDate() ?? '',
        sortBy: this.sortBy(),
        isDescending: this.isDescending(),
      })
      .pipe(finalize(() => this.isTableLoading.set(false)))
      .subscribe({
        next: (data) => {
          const items = data?.conversions?.items ?? [];
          const totalCount = data?.conversions?.totalCount ?? 0;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log("[On Fetch Conversion Listing] Items: ", items);
          // console.log("[On Fetch Conversion Listing] Total Count: ", totalCount);

          if (totalCount > 0) {
            this.fullData.set(items);
            this.totalRecords.set(totalCount);
            // AS-842: any rows that arrived in PendingValidation kick off the
            // silent validation -> POST cycle now. Toasts fire on terminal.
            items.forEach((row) => this.maybeKickOffPendingValidation(row));
          } else {
            this.fullData.set([]);
          }

          // AS-842: fire the post-submit terminal toast now that the row's real
          // status is loaded (deterministic — no timer). Safe to call with an
          // empty list: it just clears the one-shot marker without firing.
          this.firePostSubmitTerminalToast(items);
        },
        // A 401 now propagates here too, same as every other failure — the
        // service no longer swallows any of them into a fake-empty listing.
        // AUTH is staged for the login page instead of toasted here (this page
        // is about to be navigated away from by the forced-logout redirect);
        // every other class gets a screenshot-able code. The table keeps its
        // prior state either way.
        error: (error: unknown) => {
          const notice = buildPageLoadFailureNotice('XBRL_FILES', error);
          if (notice.failure === 'PERM') {
            // Not allowed to see this page's data: leave for the Dashboard with the shell toast.
            this.accessDeniedNotice.denyPageAccess();
            return;
          }
          if (notice.silent) {
            this.authFailureNotice.stage(notice.summary);
          } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
            this.messageService.add({
              key: 'process_mbrs_data',
              severity: 'error',
              summary: notice.summary,
              life: DIAGNOSTIC_TOAST_LIFE_MS,
            });
          }
        },
      });
  }

  ngOnInit(): void {
    this.debugToggle.bindGlobalShortcut(this.destroyRef);

    // ----------------------------------------------------------------------
    // TEMPORARY DIAGNOSTIC — last-tab-out discard / liveness probe.
    // Remove together with `liveness-probe-diagnostics.ts` and its call sites
    // in `filing-form-state.service.ts` / `cross-tab-sync.service.ts`.
    //
    // This page is the ONLY console that survives the repro — the conversion
    // tabs under investigation are closed as part of it, taking their devtools
    // with them. The records themselves live in `localStorage`, so they outlive
    // the tabs that wrote them; this just exposes the readers.
    //
    //   __livenessDiag()       dump the merged cross-tab timeline
    //   __livenessDiagClear()  wipe it before a fresh repro round
    // ----------------------------------------------------------------------
    // Gated on the runtime `livenessProbeDebug` flag as well as the build:
    // with the harness switched off there is nothing recorded to dump, so
    // advertising the helpers would only mislead. The functions themselves
    // stay attached either way — calling one then explains how to enable it.
    if (!environment.production && typeof window !== 'undefined') {
      (window as any).__livenessDiag = dumpLivenessDiagnostics;
      (window as any).__livenessDiagClear = clearLivenessDiagnostics;

      if (isLivenessDiagnosticsEnabled())
        console.log('[LivenessDiag] ready — run __livenessDiag() to dump, __livenessDiagClear() to reset.');
    }

    this.searchChanged.pipe(debounceTime(SEARCH_INPUT_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      this.searchQuery.set(query);
      this.first.set(0);
      this.fullData.set([]);
      this.fetchConversionListing();
    });
    this.conversionEvent.refetch$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.first.set(0);
      this.fullData.set([]);
      this.fetchConversionListing();
    });

    // Capture the post-submit marker BEFORE the first fetch so its subscribe
    // can fire the terminal toast the moment the row's status is loaded — no
    // arbitrary timer, no race. `ngAfterViewInit` clears `history.state`
    // afterwards, but the value is already held here.
    const navState = history.state as
      | { pendingValidation?: { fileName: string; conversionId: string } }
      | null;
    this.postSubmitPending = navState?.pendingValidation ?? null;

    this.fetchConversionListing(1, this.rows());

    // Single handler for both Phase-1 and Phase-2 progress events — the row
    // update is identical. Carry through `phase` from the payload for the
    // chip/interactivity gates; download stays Phase-2-only by nature (a
    // Phase 1 row never reaches a downloadable Success terminal — see the
    // isPhase1/isPhase2 note in src/types/commons.ts).
    //
    // AS-842: when the payload's status is `PendingValidation`, we update
    // the row in place (chip flips to "In Progress" — same visual as enum=2)
    // AND kick off the silent validation -> POST cycle. The chip will flip
    // again when the resolver emits the terminal value. No toast fires
    // during the PendingValidation window.
    merge(
      this.conversionProgressService.progress1st$,
      this.conversionProgressService.progress2nd$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ConversionProgressPayload) => {
        // "Last Modified" is bound to `updatedDate`, which previously only moved
        // on a full listing fetch — so a row that completed live kept a stale
        // timestamp until the user reloaded. Patch it here alongside `status`.
        // Deliberately NOT a `refetch$` trigger: that handler resets `first` and
        // blanks the table, which would yank the user off their current page on
        // every completion ping.
        const stampedAt = parseSignalRTimestamp(data.updatedAt) ?? new Date();

        const updatedFiles = this.fullData().map((file) =>
          file.id === data.conversionId
            ? {
                ...file,
                status: data.status,
                phase: data.phase ?? file.phase,
                company: data.companyName,
                updatedDate: stampedAt,
              }
            : file
        );

        this.fullData.set(updatedFiles);

        // A fresh conversion cycle (re-conversion pushing the row back to
        // InProgress while the user stays on the listing) clears the toast
        // gate so the next terminal resolution for this row can toast again.
        if (data.status === MbrsStatusEnum.InProgress) {
          this.terminalToastFired.delete(data.conversionId);
        }

        // AS-842: PendingValidation arrival -> silent validation kick-off.
        if (data.status === MbrsStatusEnum.PendingValidation) {
          const row = this.fullData().find((file) => file.id === data.conversionId);
          if (row) this.maybeKickOffPendingValidation(row);
        }
      });
  }

  /**
   * AS-842 — for any row currently in `PendingValidation`, run the silent
   * validation, swap the row's `status` in `fullData()` to the derived
   * terminal value, and fire the matching toast. POST write-back is handled
   * inside the resolver; failure leaves the row at PendingValidation
   * server-side (next listing reload self-heals).
   */
  private maybeKickOffPendingValidation(row: ConversionData): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (row.id == "INSERT_MFRS_CONVERSION_ID_HERE" || row.id == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Maybe Kick Off Pending Validation] Row: ", row);
    // }

    if (row.status !== MbrsStatusEnum.PendingValidation) return;

    this.statusResolver
      .resolve(row)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((terminal) => {
        // Update the row's status in place so the chip and interactivity
        // gates flip together. Other row fields are preserved.
        const updated = this.fullData().map((file) =>
          file.id === row.id ? { ...file, status: terminal } : file
        );
        this.fullData.set(updated);

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (row.id == "INSERT_MFRS_CONVERSION_ID_HERE" || row.id == "INSERT_MPERS_CONVERSION_ID_HERE") {
        //   console.log("[On Resolve Conversion Status | Files Listing Page] Terminal: ", terminal);
        //   console.log("[On Resolve Conversion Status | Files Listing Page] Full Data (Updated): ", updated);
        // }

        // Fire the terminal toast at most once per row per resolution. The
        // chip flip above is idempotent and always runs; only the toast is
        // gated (see `terminalToastFired`).
        if (!this.terminalToastFired.has(row.id)) {
          this.terminalToastFired.add(row.id);
          this.fireTerminalToast(row, terminal);
        }
      });
  }

  /**
   * AS-842 — fire the terminal-status toast for a row. The wording lives in
   * `ConversionListingService.getTerminalToast` (next to the chip's
   * `getDisplayStatus`) so the Success / Success* / Review distinction is
   * single-sourced and can't drift from the chip. `Success` and
   * `SuccessWithWarnings` now carry DISTINCT copy (point 2); the resolver
   * returns `null` for any non-terminal status, in which case we fire nothing.
   */
  private fireTerminalToast(row: ConversionData, terminal: MbrsStatusEnum): void {
    const toast = this.conversionListingService.getTerminalToast(terminal, row.fileName ?? '');
    if (toast) {
      this.messageService.add(toast);
    }
  }

  /**
   * AS-842 — fire the post-submit terminal toast for the just-submitted row
   * (captured in `postSubmitPending`), deterministically, once its real status
   * is present in the listing response.
   *
   * This replaces the previous `setTimeout(..., 800)` guess in
   * `ngAfterViewInit`, which raced the fetch and fired a false "ready for
   * download" toast when `fullData()` was still empty at 800ms. Running inside
   * the fetch subscribe means the row's status is known, so we never guess.
   *
   *  - Terminal-arrival row (the common case — SubmitForValidation persists a
   *    terminal status): fire the matching toast, gated by `terminalToastFired`
   *    so the resolver path can't also fire it.
   *  - Still `PendingValidation`: do nothing — `maybeKickOffPendingValidation`
   *    (already invoked for this row) owns the toast via the resolver.
   *  - Row not on this page: fire NOTHING. We never invent a status.
   *
   * One-shot: the marker is cleared on the first response so a later search /
   * pagination fetch can't replay a stale toast.
   */
  private firePostSubmitTerminalToast(items: ConversionData[]): void {
    const pending = this.postSubmitPending;
    if (!pending) return;
    this.postSubmitPending = null; // one-shot, regardless of outcome

    const row = items.find((item) => item.id === pending.conversionId);
    if (!row) return; // not on this page — don't guess a status
    if (row.status === MbrsStatusEnum.PendingValidation) return; // resolver owns it
    if (this.terminalToastFired.has(row.id)) return;

    this.terminalToastFired.add(row.id);
    this.fireTerminalToast({ ...row, fileName: pending.fileName }, row.status);
  }

  ngAfterViewInit(): void {
    // Transient post-submit toast (e.g. the "We are validating..." message
    // shipped by SubmitForValidationComponent). This carries NO terminal
    // wording — the terminal result is fired by `firePostSubmitTerminalToast`
    // from the listing fetch subscribe (or the resolver path), so it shows
    // exactly once and never races an empty `fullData()`. The matching
    // `pendingValidation` marker was already captured in `ngOnInit`.
    if (history.state.toast) {
      this.messageService.add(history.state.toast);
    }

    // Clear the navigation state so a refresh / back-nav doesn't re-toast.
    if (history.state.toast || history.state.pendingValidation) {
      history.replaceState({}, '', this.router.url);
    }
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    // Fall back to the configured size, not a stray literal — the paginator
    // always emits `rows`, but an unset fallback here would silently
    // repaginate the backend query at a size nothing else agrees on.
    const rows = event.rows ?? this.rows();

    this.first.set(first);
    this.rows.set(rows);

    const currentPage = Math.floor(first / rows) + 1;

    this.fetchConversionListing(currentPage, rows);
  }

  /**
   * AS-842 — aliased to the centralised util so the template can keep
   * calling `isDownloadable(item.status)` while the actual rule lives in
   * one place. The old method was named in inverted-logic style ("returns
   * true when NOT downloadable") which made the template hard to read; the
   * util uses positive semantics ("true when downloadable") and the
   * template has been updated to match.
   *
   * Both gates feed the runtime-config flag `nonSuccessDownloadEnabled`
   * through to the util, which is what widens them to Review / Failed. The
   * util stays DI-free; this component is the single place the config is
   * read. See `RuntimeConfig.nonSuccessDownloadEnabled` for why the flag
   * exists and when it is expected to be turned back off.
   */
  isDownloadable(status: MbrsStatusEnum): boolean {
    return isDownloadable(status, this.runtimeConfig.nonSuccessDownloadEnabled());
  }

  /** See `isDownloadable` above — same flag, menu-visibility half. */
  isMoreActionButtonVisible(status: MbrsStatusEnum): boolean {
    return isMoreActionButtonVisible(status, this.runtimeConfig.nonSuccessDownloadEnabled());
  }

  min(first: number, second: number): number {
    return Math.min(first, second);
  }

  onSearchChange(query: string): void {
    this.searchChanged.next(query);
  }

  onStatusChange(): void {
    this.first.set(0);
    this.fetchConversionListing();
  }

  /**
   * A `selectionMode="range"` picker emits mid-selection (one date, second
   * still null). The backend ignores a half-open range, so we only refetch
   * once both ends exist — otherwise the first click would clear the filter.
   */
  private isCompleteRange(value: Date[] | null): value is [Date, Date] {
    return !!value && value.length === 2 && !!value[0] && !!value[1];
  }

  onDateChange(value: Date[] | null) {
    if (this.isCompleteRange(value)) {
      this.startDate.set(value[0].toISOString());
      this.endDate.set(value[1].toISOString());
      this.first.set(0);
      this.fetchConversionListing();
    }
  }

  onModifiedDateChange(value: Date[] | null) {
    if (this.isCompleteRange(value)) {
      this.modifiedStartDate.set(value[0].toISOString());
      this.modifiedEndDate.set(value[1].toISOString());
      this.first.set(0);
      this.fetchConversionListing();
    }
  }

  onSort(event: { field: string; order: number }) {
    const columnName = event.field;
    const isDescending = event.order !== 1;

    if (this.sortBy() === columnName && this.isDescending() === isDescending) {
      return;
    }

    this.sortBy.set(columnName);
    this.isDescending.set(isDescending);
    this.first.set(0);
    this.fetchConversionListing();
  }

  clearFilters() {
    this.searchQuery.set('');
    this.selectedStatus.set('');
    this.startDate.set(null);
    this.endDate.set(null);
    this.selectedDate.set(null);
    this.modifiedStartDate.set(null);
    this.modifiedEndDate.set(null);
    this.selectedModifiedDate.set(null);
    this.first.set(0);
    this.fetchConversionListing();
  }

  onTriggerOverlay(index: number): void {
    this.openedOverlayId.set(this.openedOverlayId() === index ? null : index);
  }

  /** True while this row's zip is being regenerated, fetched and packed. */
  isDownloading(id: string): boolean {
    return this.downloadXbrlService.getDownloadSignal(id)();
  }

  /**
   * HARD FAIL — there is no `.xml` fallback. Two messages, split on one
   * question: can retrying possibly help? A naming defect is deterministic, so
   * telling the user to retry would be wrong advice.
   *
   * The toast is raised HERE and not in the service because `MessageService` is
   * component-provided on this page (see `providers` above); a root-provided
   * service would resolve a different instance and the toast would never render.
   */
  downloadXbrlFile(item: ConversionData) {
    this.openedOverlayId.set(null);
    this.downloadXbrlService
      .downloadXbrlFile(item)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error: unknown) => {
          // The conversion id belongs in the console, never in the UI.
          console.error('[xbrl-download] failed', {
            conversionId: item.id,
            xbrlFileName: item.xbrlFileName,
            fileName: item.fileName,
            error,
          });

          // This page's only toast outlet is the `key: 'process_mbrs_data'`
          // <interactive-toast> in the template; an unkeyed message is never
          // shown. Its severity switch only recognises the four
          // `custom-*` values (see interactive-toast.component.html) — plain
          // 'error' falls into the empty `@default {}` and renders nothing.
          // `summary` renders unclamped via [innerHTML]; `detail` is
          // 1-line-clamped, too short for either sentence here — so the full
          // message goes in `summary` and `detail` is omitted, matching
          // conversion-listing.service.ts's `getTerminalToast`.
          this.messageService.add({
            key: 'process_mbrs_data',
            severity: 'custom-error',
            summary:
              error instanceof XbrlDownloadNameError
                ? 'This filing has no usable file name. Please contact support with the conversion reference.'
                : (error instanceof XbrlDownloadApiError &&
                    conversionActionFailureCopy(error.errorCode)) ||
                  'Could not download the XBRL file. Please try again, or contact support if this continues.',
            life: TOAST_DURATION_EXTENDED_MS,
          });
        },
      });
  }

  deleteXbrlFile(item: ConversionData) {
    this.openedOverlayId.set(null);
    this.conversionData = item;
    this.isArchiveDialogOpen = true;
  }
}
